/**
 * GymSettingsPage Component
 *
 * Settings page for gym owners to configure their gym.
 * Uses horizontal scrollable tabs for navigation between sections.
 * Allows editing basic info, wall sections, color grades, logo, and managing setters.
 */

import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  doc,
  updateDoc,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { useGym } from '@/hooks/useGyms';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { ColorGradesManager, type ColorRenameMap } from '@/components/gym/ColorGradesManager';
import { GymLogoUpload } from '@/components/gym/GymLogoUpload';
import { FloorPlanUpload } from '@/components/gym/FloorPlanUpload';
import { SetterManagementList } from '@/components/gym/SetterManagementList';
import { InviteSetterForm } from '@/components/invitation/InviteSetterForm';
import { PendingInvitationsList } from '@/components/invitation/PendingInvitationsList';
import { OwnerManagementList, InviteOwnerForm } from '@/components/owner';
import { useOwnerInvitations } from '@/hooks/useOwnerInvitations';
import { Toggle } from '@/components/ui/Toggle';
import { HoldIcon, TagIcon } from '@/components/icons/RouteIdentificationIcons';
import type {
  ColorGrade,
  GymFloorPlan,
  TimestampLike,
  GradingSystem,
  GymLabelingMethods,
  PrimaryIdentificationMethod,
} from '@flash/shared';
import { GRADING_SYSTEMS } from '@flash/shared';

/**
 * Form data for gym basic info
 */
interface GymBasicInfoForm {
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * Section configuration
 */
interface SectionConfig {
  id: string;
  label: string;
}

const SECTIONS: SectionConfig[] = [
  { id: 'basic', label: 'Basic Info' },
  { id: 'routes', label: 'Route Settings' },
  { id: 'logo', label: 'Logo' },
  { id: 'floorplan', label: 'Floor Plan' },
  { id: 'setters', label: 'Setters' },
  { id: 'owners', label: 'Owners' },
];

/**
 * GymSettingsPage allows gym owners to configure their gym
 */
export const GymSettingsPage = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const _auth = useAuth();
  const { gym, loading: gymLoading, error: gymError, refetch: refetchGym } = useGym(gymId);
  const { getRoleAtGym, loading: membershipLoading } = useGymMemberships();

  // Get active tab from URL, default to 'basic'
  const activeTab = searchParams.get('tab') ?? 'basic';
  // Validate that the tab exists in SECTIONS, fallback to 'basic'
  const validTabIds = SECTIONS.map((s) => s.id);
  const currentTab = validTabIds.includes(activeTab) ? activeTab : 'basic';

  // Form state for basic info
  const [basicInfo, setBasicInfo] = useState<GymBasicInfoForm>({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });

  // UI state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  // Track initial load to avoid flashing loading state on refetch
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Fetch pending owner invitations for this gym
  const { invitations: ownerInvitations, loading: ownerInvitationsLoading } = useOwnerInvitations(
    gymId ?? null
  );

  // Only show loading on initial load, not on refetch
  const isInitialLoading = (gymLoading || membershipLoading) && !initialLoadComplete;
  const userRole = gymId ? getRoleAtGym(gymId) : null;
  const isOwner = userRole === 'owner';

  // Initialize form when gym loads and mark initial load as complete
  useEffect(() => {
    if (gym) {
      setBasicInfo({
        name: gym.name,
        description: gym.description ?? '',
        address: gym.address,
        city: gym.city,
        state: gym.state,
        postalCode: gym.postalCode ?? '',
        country: gym.country,
      });
      // Mark initial load as complete after gym data is loaded
      if (!initialLoadComplete) {
        setInitialLoadComplete(true);
      }
    }
  }, [gym, initialLoadComplete]);

  /**
   * Handle tab selection - updates URL search params
   */
  const handleTabClick = (tabId: string) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  /**
   * Handle basic info form input changes
   */
  const handleBasicInfoChange = (field: keyof GymBasicInfoForm, value: string) => {
    setBasicInfo((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setSaveError(null);
  };

  /**
   * Save basic info changes to Firestore
   */
  const handleSaveBasicInfo = async () => {
    if (!gymId || !gym) return;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      await updateDoc(gymRef, {
        name: basicInfo.name.trim(),
        description: basicInfo.description.trim() || null,
        address: basicInfo.address.trim(),
        city: basicInfo.city.trim(),
        state: basicInfo.state.trim(),
        postalCode: basicInfo.postalCode.trim() || null,
        country: basicInfo.country.trim(),
        updatedAt: Timestamp.now(),
      });

      setSaveSuccess(true);
      refetchGym();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setSaveError(errorMessage);
      console.error('Error saving gym basic info:', err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Update routes when colors are renamed
   * Updates colorGrade (legacy), identification.holdColor, and identification.tagColor fields
   * For hold colors, also updates tagColor if it matches (for linked mode where both share same value)
   */
  const propagateColorRenames = async (
    renames: ColorRenameMap,
    colorType: 'hold' | 'tag'
  ): Promise<number> => {
    if (!gymId || renames.size === 0) return 0;

    const db = getFirebaseDb();
    const routesRef = collection(db, 'routes');

    // Fetch all published routes for this gym once
    // Note: We use 'in' query to include both published and draft routes
    // since color renames should propagate to drafts as well
    const allRoutesQuery = query(
      routesRef,
      where('gymId', '==', gymId),
      where('status', 'in', ['published', 'draft'])
    );
    const allRoutesSnapshot = await getDocs(allRoutesQuery);

    if (allRoutesSnapshot.empty) return 0;

    // Firestore batches have a 500 operation limit
    const BATCH_SIZE = 500;
    let totalUpdated = 0;

    // Collect all updates first
    const routeUpdates: { ref: ReturnType<typeof doc>; updates: Record<string, unknown> }[] = [];

    allRoutesSnapshot.docs.forEach((routeDoc) => {
      const routeData = routeDoc.data();
      const updates: Record<string, unknown> = {};

      // Check each rename
      for (const [oldName, newName] of renames.entries()) {
        // Update legacy colorGrade field if it matches
        if (routeData['colorGrade'] === oldName) {
          updates['colorGrade'] = newName;
        }

        if (colorType === 'hold') {
          // Update identification.holdColor if it matches
          if (routeData['identification']?.['holdColor'] === oldName) {
            updates['identification.holdColor'] = newName;
          }
          // Also update identification.tagColor if it matches (for linked mode)
          // In linked mode, both holdColor and tagColor are set to the same value
          if (routeData['identification']?.['tagColor'] === oldName) {
            updates['identification.tagColor'] = newName;
          }
        } else {
          // Tag color type - only update tagColor
          if (routeData['identification']?.['tagColor'] === oldName) {
            updates['identification.tagColor'] = newName;
          }
        }
      }

      // Only update if there are actual changes
      if (Object.keys(updates).length > 0) {
        updates['updatedAt'] = Timestamp.now();
        routeUpdates.push({ ref: routeDoc.ref, updates });
      }
    });

    // Process updates in batches of 500
    for (let i = 0; i < routeUpdates.length; i += BATCH_SIZE) {
      const batchUpdates = routeUpdates.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);

      batchUpdates.forEach(({ ref, updates }) => {
        batch.update(ref, updates);
      });

      await batch.commit();
      totalUpdated += batchUpdates.length;
    }

    return totalUpdated;
  };

  /**
   * Handle color grades update (hold colors)
   * Propagates color renames to existing routes
   */
  const handleColorGradesChange = async (colorGrades: ColorGrade[], renames?: ColorRenameMap) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      // Update gym color grades
      await updateDoc(gymRef, {
        colorGrades,
        updatedAt: Timestamp.now(),
      });

      // Propagate color renames to existing routes
      if (renames && renames.size > 0) {
        const updatedCount = await propagateColorRenames(renames, 'hold');
        // eslint-disable-next-line no-console
        console.info(`Updated ${updatedCount} routes with color renames`);
      }

      refetchGym();
    } catch (err) {
      console.error('Error updating color grades:', err);
      throw err;
    }
  };

  /**
   * Handle tag color grades update (only used when colors are unlinked)
   * Propagates color renames to existing routes
   */
  const handleTagColorGradesChange = async (
    tagColorGrades: ColorGrade[],
    renames?: ColorRenameMap
  ) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      // Update gym tag color grades
      await updateDoc(gymRef, {
        tagColorGrades,
        updatedAt: Timestamp.now(),
      });

      // Propagate color renames to existing routes
      if (renames && renames.size > 0) {
        const updatedCount = await propagateColorRenames(renames, 'tag');
        // eslint-disable-next-line no-console
        console.info(`Updated ${updatedCount} routes with tag color renames`);
      }

      refetchGym();
    } catch (err) {
      console.error('Error updating tag color grades:', err);
      throw err;
    }
  };

  /**
   * Handle logo URL update
   */
  const handleLogoChange = async (logoURL: string | null) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      await updateDoc(gymRef, {
        logoURL,
        updatedAt: Timestamp.now(),
      });

      refetchGym();
    } catch (err) {
      console.error('Error updating logo:', err);
      throw err;
    }
  };

  /**
   * Handle floor plan update
   */
  const handleFloorPlanChange = async (floorPlan: GymFloorPlan | null) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      await updateDoc(gymRef, {
        floorPlan,
        updatedAt: Timestamp.now(),
      });

      refetchGym();
    } catch (err) {
      console.error('Error updating floor plan:', err);
      throw err;
    }
  };

  /**
   * Handle grading system change
   */
  const handleGradingSystemChange = async (gradingSystem: GradingSystem) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      await updateDoc(gymRef, {
        gradingSystem,
        updatedAt: Timestamp.now(),
      });

      refetchGym();
    } catch (err) {
      console.error('Error updating grading system:', err);
      throw err;
    }
  };

  /**
   * Handle require route name toggle
   */
  const handleRequireRouteNameChange = async (requireRouteName: boolean) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      const currentRequirements = gym?.routeRequirements ?? {
        requireRouteName: true,
        requirePhoto: false,
      };

      await updateDoc(gymRef, {
        routeRequirements: {
          ...currentRequirements,
          requireRouteName,
        },
        updatedAt: Timestamp.now(),
      });

      await refetchGym();
    } catch (err) {
      console.error('Error updating route name requirement:', err);
    }
  };

  /**
   * Handle allow grade modifiers toggle
   */
  const handleAllowGradeModifiersChange = async (allowGradeModifiers: boolean) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      await updateDoc(gymRef, {
        allowGradeModifiers,
        updatedAt: Timestamp.now(),
      });

      await refetchGym();
    } catch (err) {
      console.error('Error updating grade modifiers setting:', err);
    }
  };

  /**
   * Handle labeling method toggle
   */
  const handleLabelingMethodChange = async (
    method: 'holdColors' | 'tagColors',
    enabled: boolean
  ) => {
    if (!gymId || !gym) return;

    // Get current labeling methods with defaults
    const currentMethods: GymLabelingMethods = gym.labelingMethods ?? {
      holdColors: true,
      tagColors: false,
      primaryMethod: 'holdColors',
      linkColors: true,
    };

    const updatedMethods: GymLabelingMethods = {
      ...currentMethods,
      [method]: enabled,
    };

    // Prevent disabling both methods when using color-only grading
    const isColorOnly = (gym.gradingSystem ?? 'v-scale') === 'color-only';
    const wouldDisableBoth = !updatedMethods.holdColors && !updatedMethods.tagColors;

    if (isColorOnly && wouldDisableBoth) {
      // Don't allow - at least one must be enabled for color-only gyms
      return;
    }

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      // Auto-update primary method if current primary is being disabled
      if (!enabled && currentMethods.primaryMethod === method) {
        // Find another enabled method to be primary
        if (method === 'holdColors' && updatedMethods.tagColors) {
          updatedMethods.primaryMethod = 'tagColors';
        } else if (method === 'tagColors' && updatedMethods.holdColors) {
          updatedMethods.primaryMethod = 'holdColors';
        }
      }

      await updateDoc(gymRef, {
        labelingMethods: updatedMethods,
        updatedAt: Timestamp.now(),
      });

      await refetchGym();
    } catch (err) {
      console.error('Error updating labeling method:', err);
    }
  };

  /**
   * Handle primary identification method change
   */
  const handlePrimaryMethodChange = async (primaryMethod: PrimaryIdentificationMethod) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      const currentMethods: GymLabelingMethods = gym?.labelingMethods ?? {
        holdColors: true,
        tagColors: false,
        primaryMethod: 'holdColors',
        linkColors: true,
      };

      await updateDoc(gymRef, {
        labelingMethods: {
          ...currentMethods,
          primaryMethod,
        },
        updatedAt: Timestamp.now(),
      });

      await refetchGym();
    } catch (err) {
      console.error('Error updating primary method:', err);
    }
  };

  /**
   * Handle link colors toggle
   */
  const handleLinkColorsChange = async (linkColors: boolean) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      const currentMethods: GymLabelingMethods = gym?.labelingMethods ?? {
        holdColors: true,
        tagColors: false,
        primaryMethod: 'holdColors',
        linkColors: true,
      };

      await updateDoc(gymRef, {
        labelingMethods: {
          ...currentMethods,
          linkColors,
        },
        updatedAt: Timestamp.now(),
      });

      await refetchGym();
    } catch (err) {
      console.error('Error updating link colors setting:', err);
    }
  };

  /**
   * Handle require photo toggle
   */
  const handleRequirePhotoChange = async (requirePhoto: boolean) => {
    if (!gymId) return;

    try {
      const db = getFirebaseDb();
      const gymRef = doc(db, 'gyms', gymId);

      const currentRequirements = gym?.routeRequirements ?? {
        requireRouteName: true,
        requirePhoto: false,
      };

      await updateDoc(gymRef, {
        routeRequirements: {
          ...currentRequirements,
          requirePhoto,
        },
        updatedAt: Timestamp.now(),
      });

      await refetchGym();
    } catch (err) {
      console.error('Error updating photo requirement:', err);
    }
  };

  // Loading state - only show on initial load, not on refetch
  if (isInitialLoading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading settings...</p>
        </div>
      </main>
    );
  }

  // Error state
  if (gymError) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h2 style={errorTitleStyle}>Error Loading Gym</h2>
          <p style={errorTextStyle}>{gymError}</p>
          <button onClick={() => refetchGym()} style={retryButtonStyle}>
            Try Again
          </button>
        </div>
      </main>
    );
  }

  // Not found state
  if (!gym) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h2 style={errorTitleStyle}>Gym Not Found</h2>
          <p style={errorTextStyle}>{"The gym you're looking for doesn't exist."}</p>
        </div>
      </main>
    );
  }

  // Not owner - access denied
  if (!isOwner) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h2 style={errorTitleStyle}>Access Denied</h2>
          <p style={errorTextStyle}>Only the gym owner can access settings.</p>
        </div>
      </main>
    );
  }

  /**
   * Render section content based on section ID
   */
  const renderSectionContent = (sectionId: string) => {
    switch (sectionId) {
      case 'basic':
        return (
          <div style={formStyle}>
            <div style={formGroupStyle}>
              <label htmlFor="gymName" style={labelStyle}>
                Gym Name *
              </label>
              <input
                id="gymName"
                type="text"
                value={basicInfo.name}
                onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                style={inputStyle}
                maxLength={100}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="description" style={labelStyle}>
                Description
              </label>
              <textarea
                id="description"
                value={basicInfo.description}
                onChange={(e) => handleBasicInfoChange('description', e.target.value)}
                style={textareaStyle}
                maxLength={500}
                rows={3}
                placeholder="Tell climbers about your gym..."
              />
              <span style={charCountStyle}>{basicInfo.description.length}/500</span>
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="address" style={labelStyle}>
                Address *
              </label>
              <input
                id="address"
                type="text"
                value={basicInfo.address}
                onChange={(e) => handleBasicInfoChange('address', e.target.value)}
                style={inputStyle}
                required
              />
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="city" style={labelStyle}>
                  City *
                </label>
                <input
                  id="city"
                  type="text"
                  value={basicInfo.city}
                  onChange={(e) => handleBasicInfoChange('city', e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="state" style={labelStyle}>
                  State *
                </label>
                <input
                  id="state"
                  type="text"
                  value={basicInfo.state}
                  onChange={(e) => handleBasicInfoChange('state', e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="postalCode" style={labelStyle}>
                  ZIP / Postal Code
                </label>
                <input
                  id="postalCode"
                  type="text"
                  value={basicInfo.postalCode}
                  onChange={(e) => handleBasicInfoChange('postalCode', e.target.value)}
                  style={inputStyle}
                  placeholder="Optional"
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="country" style={labelStyle}>
                  Country *
                </label>
                <input
                  id="country"
                  type="text"
                  value={basicInfo.country}
                  onChange={(e) => handleBasicInfoChange('country', e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>
            </div>

            {saveError && <div style={errorMessageStyle}>{saveError}</div>}
            {saveSuccess && <div style={successMessageStyle}>Changes saved successfully!</div>}

            <button
              onClick={handleSaveBasicInfo}
              disabled={saving || !basicInfo.name.trim()}
              style={{
                ...saveButtonStyle,
                ...(saving || !basicInfo.name.trim() ? disabledButtonStyle : {}),
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        );

      case 'routes':
        return (
          <>
            {/* Grading System */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Grading System</h3>
              <p style={helpTextStyle}>
                Choose how difficulty grades are displayed for routes at your gym.
              </p>
              <div style={gradingSystemOptionsStyle}>
                {GRADING_SYSTEMS.map((system) => {
                  const isSelected = (gym.gradingSystem ?? 'v-scale') === system;
                  const labels: Record<GradingSystem, { title: string; desc: string }> = {
                    'v-scale': {
                      title: 'V-Scale (Hueco)',
                      desc: 'Grades shown as V0, V1, V2... V17',
                    },
                    font: {
                      title: 'Fontainebleau',
                      desc: 'Grades shown as 4, 5, 6a, 6b+, 7a... 9a',
                    },
                    'color-only': {
                      title: 'Color Only',
                      desc: 'No official grades, only color circuits',
                    },
                  };
                  return (
                    <button
                      type="button"
                      key={system}
                      onClick={() => handleGradingSystemChange(system)}
                      style={{
                        ...gradingSystemOptionStyle,
                        ...(isSelected ? gradingSystemOptionSelectedStyle : {}),
                      }}
                    >
                      <span style={gradingSystemTitleStyle}>{labels[system].title}</span>
                      <span style={gradingSystemDescStyle}>{labels[system].desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Route Creation Options */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Route Creation Options</h3>
              <div style={toggleRowStyle}>
                <div style={toggleLabelContainerStyle}>
                  <span style={toggleLabelTextStyle}>Route Names</span>
                  <span style={toggleHelpTextStyle}>
                    When enabled, routes have names. When disabled, routes are identified only by
                    color and grade.
                  </span>
                </div>
                <Toggle
                  checked={gym.routeRequirements?.requireRouteName ?? true}
                  onChange={(checked) => handleRequireRouteNameChange(checked)}
                  aria-label="Route names"
                />
              </div>
              <div style={toggleRowStyle}>
                <div style={toggleLabelContainerStyle}>
                  <span style={toggleLabelTextStyle}>Require Photo</span>
                  <span style={toggleHelpTextStyle}>
                    When enabled, at least one photo is required to publish a route
                  </span>
                </div>
                <Toggle
                  checked={gym.routeRequirements?.requirePhoto ?? false}
                  onChange={(checked) => handleRequirePhotoChange(checked)}
                  aria-label="Require photo"
                />
              </div>
              {/* Only show modifier toggle for V-scale grading system */}
              {(gym.gradingSystem ?? 'v-scale') === 'v-scale' && (
                <div style={toggleRowStyle}>
                  <div style={toggleLabelContainerStyle}>
                    <span style={toggleLabelTextStyle}>Allow Grade Modifiers</span>
                    <span style={toggleHelpTextStyle}>
                      Enable +/- modifiers for finer grade precision (e.g., V4+, V5-)
                    </span>
                  </div>
                  <Toggle
                    checked={gym.allowGradeModifiers ?? false}
                    onChange={(checked) => handleAllowGradeModifiersChange(checked)}
                    aria-label="Allow grade modifiers"
                  />
                </div>
              )}
            </div>

            {/* Route Identification Methods */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Route Identification Methods</h3>
              <p style={helpTextStyle}>
                Configure how routes are visually identified at your gym. Enable the methods you
                use.
              </p>
              {(() => {
                const holdColorsEnabled = gym.labelingMethods?.holdColors ?? true;
                const tagColorsEnabled = gym.labelingMethods?.tagColors ?? false;
                const bothMethodsEnabled = holdColorsEnabled && tagColorsEnabled;
                const isLinked = bothMethodsEnabled && (gym.labelingMethods?.linkColors ?? true);
                const isColorOnly = (gym.gradingSystem ?? 'v-scale') === 'color-only';

                // For color-only gyms, at least one method must be enabled
                const canDisableHoldColors = !isColorOnly || tagColorsEnabled;
                const canDisableTagColors = !isColorOnly || holdColorsEnabled;

                return (
                  <>
                    <div style={toggleRowStyle}>
                      <div style={toggleLabelContainerStyle}>
                        <span style={toggleLabelTextStyle}>Hold Colors</span>
                        <span style={toggleHelpTextStyle}>
                          Routes identified by monochromatic holds (all holds same color)
                        </span>
                        {isColorOnly && !canDisableHoldColors && (
                          <span style={toggleDisabledHintStyle}>
                            At least one method required for color grading
                          </span>
                        )}
                      </div>
                      <Toggle
                        checked={holdColorsEnabled}
                        onChange={(checked) => handleLabelingMethodChange('holdColors', checked)}
                        disabled={holdColorsEnabled && !canDisableHoldColors}
                        aria-label="Enable hold colors"
                      />
                    </div>
                    <div style={toggleRowStyle}>
                      <div style={toggleLabelContainerStyle}>
                        <span style={toggleLabelTextStyle}>Tag Colors</span>
                        <span style={toggleHelpTextStyle}>
                          Routes identified by colored tape or tags (allows multiple routes on
                          shared holds)
                        </span>
                        {isColorOnly && !canDisableTagColors && (
                          <span style={toggleDisabledHintStyle}>
                            At least one method required for color grading
                          </span>
                        )}
                      </div>
                      <Toggle
                        checked={tagColorsEnabled}
                        onChange={(checked) => handleLabelingMethodChange('tagColors', checked)}
                        disabled={tagColorsEnabled && !canDisableTagColors}
                        aria-label="Enable tag colors"
                      />
                    </div>

                    {/* Link Colors toggle - only shown when both methods are enabled */}
                    {bothMethodsEnabled && (
                      <div style={toggleRowStyle}>
                        <div style={toggleLabelContainerStyle}>
                          <span style={toggleLabelTextStyle}>Link Hold & Tag Colors</span>
                          <span style={toggleHelpTextStyle}>
                            When enabled, routes use a single color for both holds and tags. When
                            disabled, holds and tags can have different colors.
                          </span>
                        </div>
                        <Toggle
                          checked={gym.labelingMethods?.linkColors ?? true}
                          onChange={(checked) => handleLinkColorsChange(checked)}
                          aria-label="Link hold and tag colors"
                        />
                      </div>
                    )}

                    {/* Primary Identification Method - only shown when unlinked (colors can differ) */}
                    {bothMethodsEnabled && !isLinked && (
                      <div style={dropdownRowStyle}>
                        <div style={toggleLabelContainerStyle}>
                          <span style={toggleLabelTextStyle}>Primary Identification Method</span>
                          <span style={toggleHelpTextStyle}>
                            Determines which color appears on map pins and route lists
                          </span>
                        </div>
                        <select
                          value={gym.labelingMethods?.primaryMethod ?? 'holdColors'}
                          onChange={(e) =>
                            handlePrimaryMethodChange(e.target.value as PrimaryIdentificationMethod)
                          }
                          style={selectStyle}
                          aria-label="Primary identification method"
                        >
                          <option value="holdColors">Hold Colors</option>
                          <option value="tagColors">Tag Colors</option>
                        </select>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Gym Colors Section(s) */}
            {(() => {
              const holdColorsEnabled = gym.labelingMethods?.holdColors ?? true;
              const tagColorsEnabled = gym.labelingMethods?.tagColors ?? false;
              const bothEnabled = holdColorsEnabled && tagColorsEnabled;
              const isLinked = bothEnabled && (gym.labelingMethods?.linkColors ?? true);

              // When both enabled but NOT linked: show two separate sections
              if (bothEnabled && !isLinked) {
                const handleHoldTransfer = async (color: ColorGrade, action: 'copy' | 'move') => {
                  const tagColors = gym.tagColorGrades ?? [];
                  // Check for duplicate
                  if (
                    tagColors.some(
                      (c) => c.displayName.toLowerCase() === color.displayName.toLowerCase()
                    )
                  ) {
                    return; // Already exists in target
                  }
                  const newTagColors = [...tagColors, color];
                  await handleTagColorGradesChange(newTagColors);
                  if (action === 'move') {
                    const newHoldColors = gym.colorGrades.filter(
                      (c) => c.displayName !== color.displayName
                    );
                    await handleColorGradesChange(newHoldColors);
                  }
                };

                const handleTagTransfer = async (color: ColorGrade, action: 'copy' | 'move') => {
                  const holdColors = gym.colorGrades;
                  // Check for duplicate
                  if (
                    holdColors.some(
                      (c) => c.displayName.toLowerCase() === color.displayName.toLowerCase()
                    )
                  ) {
                    return; // Already exists in target
                  }
                  const newHoldColors = [...holdColors, color];
                  await handleColorGradesChange(newHoldColors);
                  if (action === 'move') {
                    const newTagColors = (gym.tagColorGrades ?? []).filter(
                      (c) => c.displayName !== color.displayName
                    );
                    await handleTagColorGradesChange(newTagColors);
                  }
                };

                return (
                  <>
                    {/* Hold Colors Section */}
                    <div style={subsectionStyle}>
                      <h3 style={subsectionTitleStyle}>Hold Colors</h3>
                      <p style={helpTextStyle}>
                        Define the hold colors used for routes at your gym. These are the actual
                        colors of the climbing holds.
                      </p>
                      <ColorGradesManager
                        colorGrades={gym.colorGrades}
                        onSave={handleColorGradesChange}
                        gymId={gymId!}
                        gradingSystem={gym.gradingSystem ?? 'v-scale'}
                        onTransferColor={handleHoldTransfer}
                        targetLabel="Tag Colors"
                      />
                    </div>

                    {/* Tag Colors Section */}
                    <div style={subsectionStyle}>
                      <h3 style={subsectionTitleStyle}>Tag Colors</h3>
                      <p style={helpTextStyle}>
                        Define the tag/tape colors used to mark routes at your gym. These may differ
                        from hold colors.
                      </p>
                      <ColorGradesManager
                        colorGrades={gym.tagColorGrades ?? []}
                        onSave={handleTagColorGradesChange}
                        gymId={gymId!}
                        gradingSystem={gym.gradingSystem ?? 'v-scale'}
                        onTransferColor={handleTagTransfer}
                        targetLabel="Hold Colors"
                      />
                    </div>
                  </>
                );
              }

              // Single section for: linked colors, only holds, or only tags
              const sectionTitle =
                bothEnabled && isLinked
                  ? 'Route Colors'
                  : holdColorsEnabled
                    ? 'Hold Colors'
                    : tagColorsEnabled
                      ? 'Tag Colors'
                      : 'Route Colors';

              const sectionDesc =
                bothEnabled && isLinked
                  ? 'Define the route colors at your gym. Each color applies to both holds and tags.'
                  : holdColorsEnabled
                    ? 'Define the hold colors available for routes at your gym.'
                    : tagColorsEnabled
                      ? 'Define the tag/tape colors available for routes at your gym.'
                      : 'Enable at least one identification method above to configure colors.';

              return (
                <div style={subsectionStyle}>
                  <h3 style={subsectionTitleStyle}>{sectionTitle}</h3>
                  <p style={helpTextStyle}>{sectionDesc}</p>
                  <ColorGradesManager
                    colorGrades={gym.colorGrades}
                    onSave={handleColorGradesChange}
                    gymId={gymId!}
                    gradingSystem={gym.gradingSystem ?? 'v-scale'}
                  />
                </div>
              );
            })()}

            {/* Color Preview Section */}
            {(() => {
              const holdColorsEnabled = gym.labelingMethods?.holdColors ?? true;
              const tagColorsEnabled = gym.labelingMethods?.tagColors ?? false;
              const bothEnabled = holdColorsEnabled && tagColorsEnabled;
              const isLinked = bothEnabled && (gym.labelingMethods?.linkColors ?? true);
              const hasHoldColors = gym.colorGrades && gym.colorGrades.length > 0;
              const hasTagColors = isLinked
                ? hasHoldColors
                : gym.tagColorGrades && gym.tagColorGrades.length > 0;

              // Don't show preview section if no identification methods are enabled
              if (!holdColorsEnabled && !tagColorsEnabled) return null;

              return (
                <div style={subsectionStyle}>
                  <h3 style={subsectionTitleStyle}>Color Preview</h3>
                  <p style={helpTextStyle}>
                    How your colors will appear on routes based on your identification methods.
                  </p>

                  {/* Hold Colors Preview */}
                  {holdColorsEnabled && (
                    <div style={colorPreviewGroupStyle}>
                      <span style={colorPreviewLabelStyle}>Hold Colors:</span>
                      {hasHoldColors ? (
                        <div style={colorPreviewRowStyle}>
                          {gym.colorGrades.map((cg, idx) => (
                            <div key={idx} style={colorPreviewItemStyle}>
                              <HoldIcon color={cg.color} size={28} />
                              <span style={colorPreviewNameStyle}>{cg.displayName}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={emptyColorsTextStyle}>No hold colors defined</p>
                      )}
                    </div>
                  )}

                  {/* Tag Colors Preview */}
                  {tagColorsEnabled && (
                    <div style={colorPreviewGroupStyle}>
                      <span style={colorPreviewLabelStyle}>Tag Colors:</span>
                      {hasTagColors ? (
                        <div style={colorPreviewRowStyle}>
                          {(isLinked ? gym.colorGrades : (gym.tagColorGrades ?? [])).map(
                            (cg, idx) => (
                              <div key={idx} style={colorPreviewItemStyle}>
                                <TagIcon color={cg.color} size={28} />
                                <span style={colorPreviewNameStyle}>{cg.displayName}</span>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p style={emptyColorsTextStyle}>No tag colors defined</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        );

      case 'logo':
        return (
          <>
            <p style={sectionDescStyle}>
              Upload your gym logo. It will be displayed in the gym directory and on your gym page.
            </p>
            <GymLogoUpload
              currentLogoUrl={gym.logoURL}
              onLogoChange={handleLogoChange}
              gymId={gymId!}
            />
          </>
        );

      case 'floorplan':
        return (
          <>
            <p style={sectionDescStyle}>
              Upload a floor plan image of your gym. You can then define clickable regions for each
              wall section, allowing climbers to browse routes by tapping areas on the map.
            </p>
            <FloorPlanUpload
              currentFloorPlan={gym.floorPlan}
              onFloorPlanChange={handleFloorPlanChange}
              gymId={gymId!}
            />
          </>
        );

      case 'setters':
        return (
          <>
            <p style={sectionDescStyle}>View and manage the route setters at your gym.</p>

            {/* Invite Form */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Invite New Setter</h3>
              <InviteSetterForm gymId={gymId!} />
            </div>

            {/* Pending Invitations */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Pending Invitations</h3>
              <PendingInvitationsList gymId={gymId!} />
            </div>

            {/* Current Setters */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Current Setters</h3>
              <SetterManagementList gymId={gymId!} />
            </div>
          </>
        );

      case 'owners':
        return (
          <>
            <p style={sectionDescStyle}>
              View and manage co-owners for your gym. Co-owners have full access to gym settings.
            </p>

            {/* Invite Form */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Invite New Owner</h3>
              <InviteOwnerForm gymId={gymId!} />
            </div>

            {/* Pending Owner Invitations */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Pending Owner Invitations</h3>
              {ownerInvitationsLoading ? (
                <p style={loadingTextInlineStyle}>Loading invitations...</p>
              ) : ownerInvitations.length === 0 ? (
                <p style={emptyStateTextStyle}>No pending owner invitations.</p>
              ) : (
                <div style={invitationsListStyle}>
                  {ownerInvitations.map((invitation) => {
                    const expiresAt = toDate(invitation.expiresAt);
                    const isExpired = new Date() > expiresAt;
                    return (
                      <div key={invitation.id} style={invitationItemStyle}>
                        <div style={invitationInfoStyle}>
                          <span style={invitationEmailStyle}>{invitation.email}</span>
                          <span style={invitationDateStyle}>
                            {isExpired ? 'Expired' : `Expires: ${expiresAt.toLocaleDateString()}`}
                          </span>
                        </div>
                        <span
                          style={{
                            ...invitationStatusStyle,
                            ...(isExpired ? invitationExpiredStyle : {}),
                          }}
                        >
                          {isExpired ? 'Expired' : 'Pending'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Current Owners */}
            <div style={subsectionStyle}>
              <h3 style={subsectionTitleStyle}>Current Owners</h3>
              <OwnerManagementList gymId={gymId!} />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <main style={containerStyle}>
      <div style={innerContainerStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <button onClick={() => navigate(-1)} style={backButtonStyle} aria-label="Back to gym">
            <span style={backArrowStyle}>&#8592;</span>
            Back to Gym
          </button>
          <h1 style={pageTitleStyle}>Gym Settings</h1>
          <p style={gymNameStyle}>{gym.name}</p>
        </header>

        {/* Scrollable Horizontal Tabs */}
        <div style={tabsWrapperStyle}>
          <div ref={tabsRef} style={tabsContainerStyle}>
            {SECTIONS.map((section) => {
              const isActive = currentTab === section.id;
              return (
                <button
                  key={section.id}
                  className="settings-tab-button"
                  onClick={() => handleTabClick(section.id)}
                  onMouseDown={(e) => e.preventDefault()}
                  style={{
                    ...tabButtonStyle,
                    ...(isActive ? tabButtonActiveStyle : {}),
                  }}
                  aria-selected={isActive}
                  role="tab"
                >
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div style={tabContentContainerStyle}>
          <div style={tabContentCardStyle}>{renderSectionContent(currentTab)}</div>
        </div>
      </div>
    </main>
  );
};

/**
 * Helper to convert TimestampLike to Date
 */
const toDate = (timestamp: TimestampLike): Date => {
  if (timestamp instanceof Date) {
    return timestamp;
  }
  // Firestore Timestamp has toDate method
  if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  // Fallback: treat as serialized timestamp
  return new Date(timestamp as unknown as string);
};

/**
 * Component styles using CSS variables for dark mode support
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
  padding: '20px',
  overflowY: 'auto',
};

const innerContainerStyle: CSSProperties = {
  maxWidth: '600px',
  margin: '0 auto',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
  padding: '20px',
  textAlign: 'center',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '16px',
};

const retryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
  marginBottom: '12px',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const backButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 12px',
  marginBottom: '16px',
  backgroundColor: 'transparent',
  color: 'var(--color-text-secondary)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  marginLeft: '-12px',
};

const backArrowStyle: CSSProperties = {
  fontSize: '1.125rem',
};

const pageTitleStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: '4px',
};

const gymNameStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
};

const tabsWrapperStyle: CSSProperties = {
  marginBottom: '16px',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: 'var(--color-bg-secondary)',
  marginLeft: '-20px',
  marginRight: '-20px',
  paddingLeft: '20px',
  paddingRight: '20px',
};

const tabsContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  overflowX: 'auto',
  paddingBottom: '12px',
  paddingTop: '4px',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE/Edge
};

const tabButtonStyle: CSSProperties = {
  flexShrink: 0,
  padding: '10px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-primary)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--color-border)',
  borderRadius: '20px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
  whiteSpace: 'nowrap',
  outline: 'none',
  boxShadow: 'none',
  WebkitTapHighlightColor: 'transparent',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
};

const tabButtonActiveStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  borderColor: 'var(--color-primary)',
};

const tabContentContainerStyle: CSSProperties = {
  flex: 1,
};

const tabContentCardStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow-md)',
  padding: '20px',
};

const sectionDescStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '16px',
};

const subsectionStyle: CSSProperties = {
  marginBottom: '24px',
};

const subsectionTitleStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const formStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const formGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  flex: 1,
};

const formRowStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
};

const labelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const inputStyle: CSSProperties = {
  padding: '10px 14px',
  fontSize: '1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-primary)',
};

const textareaStyle: CSSProperties = {
  padding: '10px 14px',
  fontSize: '1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const charCountStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  textAlign: 'right',
};

const saveButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
  alignSelf: 'flex-start',
};

const disabledButtonStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  cursor: 'not-allowed',
};

const errorMessageStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: `1px solid var(--color-error)`,
  color: 'var(--color-error)',
  fontSize: '0.875rem',
};

const successMessageStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-success-bg)',
  border: `1px solid var(--color-success)`,
  color: 'var(--color-badge-success-text)',
  fontSize: '0.875rem',
};

/**
 * Owner invitations styles
 */
const loadingTextInlineStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  fontStyle: 'italic',
};

const emptyStateTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-muted)',
};

const invitationsListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const invitationItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
};

const invitationInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const invitationEmailStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const invitationDateStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
};

const invitationStatusStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  color: 'var(--color-badge-warning-text)',
};

const invitationExpiredStyle: CSSProperties = {
  backgroundColor: 'var(--color-badge-warning-bg)',
  color: 'var(--color-error)',
};

/**
 * Route Settings styles
 */
const helpTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '16px',
};

const gradingSystemOptionsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const gradingSystemOptionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'var(--color-border)',
  borderRadius: '12px',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'all 0.2s',
  outline: 'none',
};

const gradingSystemOptionSelectedStyle: CSSProperties = {
  backgroundColor: 'var(--color-badge-info-bg)',
  borderColor: 'var(--color-primary)',
};

const gradingSystemTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const gradingSystemDescStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
};

const toggleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 0',
  borderBottom: '1px solid var(--color-border)',
};

const toggleLabelContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
  marginRight: '16px',
};

const toggleLabelTextStyle: CSSProperties = {
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const toggleHelpTextStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
};

const toggleDisabledHintStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-warning, #f59e0b)',
  fontStyle: 'italic',
  marginTop: '2px',
};

const dropdownRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 0',
  borderBottom: '1px solid var(--color-border)',
};

const selectStyle: CSSProperties = {
  padding: '8px 12px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-primary)',
  minWidth: '140px',
  cursor: 'pointer',
};

// Color Preview styles
const colorPreviewGroupStyle: CSSProperties = {
  marginTop: '16px',
};

const colorPreviewLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  marginBottom: '8px',
};

const colorPreviewRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
};

const colorPreviewItemStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  padding: '8px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  minWidth: '70px',
};

const colorPreviewNameStyle: CSSProperties = {
  fontSize: '0.6875rem',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
  maxWidth: '60px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const emptyColorsTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-muted)',
  fontStyle: 'italic',
  margin: '8px 0',
};

export default GymSettingsPage;
